"use client";

import { useEffect, useRef } from "react";

interface ProposalTrackerProps {
  slug: string;
  sectionIds: string[];
  // Jeton ?t= de l'URL (voir OffreData.trackingToken) : envoyé tel quel à
  // /api/track/view/start, qui le compare au jeton stocké sur l'offre pour
  // distinguer un clic direct depuis le lien envoyé d'une visite arrivée
  // autrement.
  linkToken: string | null;
}

// Suivi de consultation, jamais monté pour une session super-admin (voir
// [slug]/page.tsx : rendu conditionné à !isAdmin, donc ce code n'atteint même
// pas le navigateur dans ce cas). Mesure le temps de lecture réel (suspendu
// quand l'onglet est en arrière-plan), le scroll maximum atteint, le temps
// passé sur CHAQUE section (même technique d'IntersectionObserver que
// section-nav.tsx pour désigner la section "active" à un instant donné) et
// la dernière section visible avant le départ (= le point de sortie). Envoi
// via sendBeacon, seule méthode fiable au moment où l'onglet se ferme (ne
// supporte que POST, jamais PATCH, d'où l'upsert par POST côté API) :
// périodiquement pendant la consultation, puis une dernière fois au départ.
export function ProposalTracker({ slug, sectionIds, linkToken }: ProposalTrackerProps) {
  const viewIdRef = useRef<string | null>(null);

  // Chronomètre global (temps sur la page), suspendu hors visibilité.
  const visibleSinceRef = useRef<number | null>(
    typeof document !== "undefined" && document.visibilityState === "visible"
      ? performance.now()
      : null
  );
  const accumulatedSecondsRef = useRef(0);

  // Chronomètre par section : même principe, mais la "section active" change
  // au fil du scroll (désignée par l'IntersectionObserver, section la plus
  // haute actuellement visible, comme section-nav.tsx). À chaque changement
  // de section active OU de visibilité de l'onglet, on fige le temps écoulé
  // sur la section qui vient de cesser d'être active avant de redémarrer.
  const sectionDurationsRef = useRef<Record<string, number>>({});
  const activeSectionRef = useRef<string | null>(null);
  const activeSectionSinceRef = useRef<number | null>(null);
  const lastSectionSeenRef = useRef<string | null>(null);

  const maxScrollRef = useRef(0);

  useEffect(() => {
    let cancelled = false;

    fetch("/api/track/view/start", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ slug, linkToken, userAgent: navigator.userAgent }),
    })
      .then((res) => res.json())
      .then((json) => {
        if (!cancelled && json?.viewId) viewIdRef.current = json.viewId;
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [slug, linkToken]);

  useEffect(() => {
    function currentDurationSeconds(): number {
      let seconds = accumulatedSecondsRef.current;
      if (visibleSinceRef.current !== null) {
        seconds += (performance.now() - visibleSinceRef.current) / 1000;
      }
      return Math.round(seconds);
    }

    // Fige le temps écoulé sur la section active dans l'accumulateur, sans
    // changer quelle section est active (utilisé aussi bien sur un
    // changement de section que sur une mise en veille de l'onglet).
    function flushActiveSection() {
      const id = activeSectionRef.current;
      if (id !== null && activeSectionSinceRef.current !== null) {
        const elapsed = (performance.now() - activeSectionSinceRef.current) / 1000;
        sectionDurationsRef.current[id] = (sectionDurationsRef.current[id] || 0) + elapsed;
      }
      activeSectionSinceRef.current = null;
    }

    function setActiveSection(id: string | null) {
      if (id === activeSectionRef.current) return;
      flushActiveSection();
      activeSectionRef.current = id;
      if (id) {
        lastSectionSeenRef.current = id;
        // Enregistre la section comme "vue" dès qu'elle devient active, même
        // si l'onglet repasse en arrière-plan avant d'accumuler quoi que ce
        // soit (une valeur à 0 reste une section vue, juste très vite).
        sectionDurationsRef.current[id] = sectionDurationsRef.current[id] || 0;
        if (document.visibilityState === "visible") {
          activeSectionSinceRef.current = performance.now();
        }
      }
    }

    function sectionDurationsSnapshot(): Record<string, number> {
      const snapshot: Record<string, number> = {};
      for (const [id, seconds] of Object.entries(sectionDurationsRef.current)) {
        snapshot[id] = Math.round(seconds);
      }
      const activeId = activeSectionRef.current;
      if (activeId !== null && activeSectionSinceRef.current !== null) {
        const elapsed = (performance.now() - activeSectionSinceRef.current) / 1000;
        snapshot[activeId] = Math.round((snapshot[activeId] || 0) + elapsed);
      }
      return snapshot;
    }

    function sendBeacon() {
      const viewId = viewIdRef.current;
      if (!viewId) return;
      const payload = JSON.stringify({
        viewId,
        durationSeconds: currentDurationSeconds(),
        maxScrollPercent: maxScrollRef.current,
        sectionDurations: sectionDurationsSnapshot(),
        lastSectionSeen: lastSectionSeenRef.current,
      });
      navigator.sendBeacon("/api/track/view/update", payload);
    }

    // Scroll max (throttlé via requestAnimationFrame).
    let scrollTicking = false;
    function onScroll() {
      if (scrollTicking) return;
      scrollTicking = true;
      requestAnimationFrame(() => {
        const doc = document.documentElement;
        const scrollable = doc.scrollHeight - doc.clientHeight;
        const percent = scrollable > 0 ? (window.scrollY / scrollable) * 100 : 100;
        maxScrollRef.current = Math.max(maxScrollRef.current, Math.min(100, Math.round(percent)));
        scrollTicking = false;
      });
    }
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();

    // Section active : même logique que section-nav.tsx (rootMargin
    // identique), la section la plus haute actuellement visible devient
    // "active" et son chronomètre démarre.
    const elements = sectionIds
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => el !== null);
    let observer: IntersectionObserver | null = null;
    if (elements.length > 0) {
      observer = new IntersectionObserver(
        (entries) => {
          const visible = entries
            .filter((e) => e.isIntersecting)
            .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
          setActiveSection(visible[0]?.target.id ?? activeSectionRef.current);
        },
        { rootMargin: "-15% 0px -70% 0px", threshold: 0 }
      );
      elements.forEach((el) => observer!.observe(el));
    }

    // Temps de lecture réel (page ET section active) : suspendu quand
    // l'onglet passe en arrière-plan, pour qu'un onglet oublié ouvert ne
    // gonfle pas artificiellement le temps de lecture.
    function onVisibilityChange() {
      if (document.visibilityState === "hidden") {
        if (visibleSinceRef.current !== null) {
          accumulatedSecondsRef.current += (performance.now() - visibleSinceRef.current) / 1000;
          visibleSinceRef.current = null;
        }
        flushActiveSection();
        sendBeacon();
      } else {
        visibleSinceRef.current = performance.now();
        if (activeSectionRef.current !== null) {
          activeSectionSinceRef.current = performance.now();
        }
      }
    }
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pagehide", sendBeacon);

    const interval = window.setInterval(() => {
      if (document.visibilityState === "visible") sendBeacon();
    }, 10000);

    return () => {
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pagehide", sendBeacon);
      window.clearInterval(interval);
      observer?.disconnect();
    };
  }, [sectionIds]);

  return null;
}
